import { useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ActionBar from '../components/ActionBar';
import SoapNote from '../components/SoapNote';
import VitalsGrid from '../components/VitalsGrid';
import SbarNote from '../components/SbarNote';
import AdmissionNote from '../components/AdmissionNote';
import DischargeNote from '../components/DischargeNote';
import MedicationAdminNote from '../components/MedicationAdminNote';
import FluidBalanceNote from '../components/FluidBalanceNote';
import CarePlanNote from '../components/carePlan/CarePlanNote';
import PatientHeaderBar from '../components/review/PatientHeaderBar';
import NoteTypeSectionHeader from '../components/review/NoteTypeSectionHeader';
import { CareTimeEditor } from '../components/NoteTimingMeta';
import TranscriptViewer from '../components/TranscriptViewer';
import NoteApplyingSkeleton from '../components/review/NoteApplyingSkeleton';
import { REVIEW_PAGE_BG } from '../components/review/reviewStyles';
import { getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { useLanguage } from '../contexts/LanguageContext';
import { useSession } from '../contexts/SessionContext';
import { HoldToRecordButton } from '../contexts/HoldToRecordContext';
import { ReviewModalFloatingSlotContext } from '../layouts/ReviewModal';
import {
  cleanTranscript,
  detectNoteTypes,
  generateNote,
  updateNurseNotesFromCorrection,
} from '../services/openAIService';
import { validateNote } from '../utils/jsonValidator';
import {
  applyLateEntryMetadata,
  deriveCareTime,
  getCurrentCareTime,
} from '../utils/lateEntry';
import { summarizeReviewQueuePatients } from '../utils/mergeReviewQueue';
import { applyKnownPatientToNote, toNotePatientContext } from '../utils/patientContext';
import { applyKnownPatientToHandoverNote } from '../utils/handoverUtils';
import { getNoteMedicationSubmitBlocker } from '../utils/drugCategory';

function cloneQueue(queue) {
  return (queue || []).map((item) => ({
    ...item,
    generatedNotes: (item.generatedNotes || []).map((entry) => ({
      ...entry,
      note: entry.note ? { ...entry.note } : entry.note,
    })),
  }));
}

function renderNoteEditor(type, note, onChange, { hideExtractedVitals = false } = {}) {
  switch (type) {
    case 'progress_note':
      return (
        <SoapNote
          note={note}
          onChange={onChange}
          hideExtractedVitals={hideExtractedVitals}
          showTypeHeader={false}
        />
      );
    case 'vitals':
      return <VitalsGrid note={note} onChange={onChange} />;
    case 'handover':
      return <SbarNote note={note} onChange={onChange} showTypeHeader={false} />;
    case 'admission':
      return <AdmissionNote note={note} onChange={onChange} />;
    case 'discharge':
      return <DischargeNote note={note} onChange={onChange} showTypeHeader={false} />;
    case 'medication_admin':
      return <MedicationAdminNote note={note} onChange={onChange} />;
    case 'fluid_balance':
      return <FluidBalanceNote note={note} onChange={onChange} />;
    case 'care_plan':
      return <CarePlanNote note={note} onChange={onChange} />;
    default:
      return null;
  }
}

function buildSavePayload({
  item,
  entry,
  entryIndex,
  patientMeta,
  documentationTime,
  language,
  admittedPatients,
}) {
  const withDefaults = {
    ...(entry.note || {}),
    datetime: documentationTime,
    care_time:
      entry.note?.care_time
      || deriveCareTime(entry.note)
      || getCurrentCareTime(),
    care_day_offset:
      entry.note?.care_day_offset === 0 || entry.note?.care_day_offset === 1
        ? entry.note.care_day_offset
        : null,
  };
  const withTiming = applyLateEntryMetadata(withDefaults, documentationTime);
  const roster = item.patientId
    ? getPatientById(item.patientId, admittedPatients)
    : null;

  return {
    id: `${item.encounterGroupId || item.noteId || 'multi'}-${entry.type}-${entryIndex}`,
    patient_id: item.patientId || roster?.id || null,
    patient_name: roster
      ? getPatientDisplayName(roster, language)
      : patientMeta.name,
    patient_room: roster?.room || patientMeta.room || null,
    note_type: entry.type,
    datetime: documentationTime,
    documentation_time: documentationTime,
    care_time: withTiming.care_time,
    care_day_offset: withTiming.care_day_offset,
    is_late_entry: withTiming.is_late_entry,
    late_entry_diff_minutes: withTiming.late_entry_diff_minutes,
    encounter_group_id: item.encounterGroupId || item.noteId,
    note_content: withTiming,
    openaiNote: withTiming,
    transcript: item.transcriptCleaned || item.transcriptRaw || '',
    transcript_raw: item.transcriptRaw || item.transcriptCleaned || '',
    transcript_cleaned: item.transcriptCleaned || item.transcriptRaw || '',
    pdf_saved: false,
    model_used: 'openai',
    generatedNotes: item.generatedNotes,
    cortiFailed: true,
  };
}

function findSectionIndexForTranscript(sections, patients, transcript, admittedPatients) {
  const lower = String(transcript || '').toLowerCase();
  for (let index = 0; index < patients.length; index += 1) {
    const meta = patients[index];
    const name = String(meta?.name || '').toLowerCase();
    const room = String(meta?.room || '').toLowerCase();
    const first = name.split(/\s+/)[0];
    if (room && new RegExp(`\\broom\\s*#?\\s*${room}\\b`).test(lower)) return index;
    if (first && first.length >= 3 && lower.includes(first)) return index;
    if (name && lower.includes(name)) return index;
  }

  // Fall back to roster match via patientId on sections
  for (let index = 0; index < sections.length; index += 1) {
    const patient = sections[index]?.patientId
      ? getPatientById(sections[index].patientId, admittedPatients)
      : null;
    if (!patient) continue;
    const nameEn = String(patient.nameEn || '').toLowerCase();
    const first = nameEn.split(/\s+/)[0];
    if (patient.room && new RegExp(`\\broom\\s*#?\\s*${patient.room}\\b`, 'i').test(lower)) {
      return index;
    }
    if (first && first.length >= 3 && lower.includes(first)) return index;
  }

  return 0;
}

/**
 * One-page multi-patient review for typed queues (vitals, MAR, progress, etc.).
 * Matches handover multi-patient chrome: avatar stack + note-type icons,
 * with each patient keeping their detected note type(s).
 */
export default function MultiPatientQueueReview({
  queue,
  onSaveNote,
  onDiscard,
  onSaveComplete,
}) {
  const { language, t } = useLanguage();
  const { admittedPatients } = useSession();
  const floatingSlot = useContext(ReviewModalFloatingSlotContext);
  const [sections, setSections] = useState(() => cloneQueue(queue));
  const [saveError, setSaveError] = useState('');
  const [micBusy, setMicBusy] = useState(false);
  const documentationTime = useMemo(() => new Date().toISOString(), []);

  const patients = useMemo(
    () => summarizeReviewQueuePatients(sections, admittedPatients, language),
    [sections, admittedPatients, language]
  );

  const noteTypes = useMemo(() => {
    const types = [];
    sections.forEach((item) => {
      (item.generatedNotes || []).forEach((entry) => {
        if (entry?.note && entry.type && !types.includes(entry.type)) {
          types.push(entry.type);
        }
      });
    });
    return types;
  }, [sections]);

  const sharedTranscriptRaw = useMemo(() => {
    const fromSections = sections
      .map((item) => item.transcriptRaw || '')
      .find((text) => text.trim());
    // Prefer the longest raw transcript (full recording stamped on each item)
    const longest = sections.reduce((best, item) => {
      const text = String(item.transcriptRaw || '');
      return text.length > best.length ? text : best;
    }, '');
    return longest || fromSections || '';
  }, [sections]);

  const sharedTranscriptCleaned = useMemo(() => {
    const longest = sections.reduce((best, item) => {
      const text = String(item.transcriptCleaned || '');
      return text.length > best.length ? text : best;
    }, '');
    return longest || sharedTranscriptRaw;
  }, [sections, sharedTranscriptRaw]);

  const updateNote = (sectionIndex, noteType, updatedNote) => {
    setSections((prev) =>
      prev.map((item, index) => {
        if (index !== sectionIndex) return item;
        return {
          ...item,
          generatedNotes: (item.generatedNotes || []).map((entry) =>
            entry.type === noteType ? { ...entry, note: updatedNote } : entry
          ),
        };
      })
    );
  };

  const updateCareTime = (sectionIndex, noteType, timingUpdate) => {
    setSections((prev) =>
      prev.map((item, index) => {
        if (index !== sectionIndex) return item;
        return {
          ...item,
          generatedNotes: (item.generatedNotes || []).map((entry) => {
            if (entry.type !== noteType || !entry.note) return entry;
            return {
              ...entry,
              note: {
                ...entry.note,
                ...timingUpdate,
              },
            };
          }),
        };
      })
    );
  };

  const handleSave = () => {
    setSaveError('');

    for (const item of sections) {
      for (const entry of item.generatedNotes || []) {
        if (entry.type !== 'medication_admin' || !entry.note) continue;
        const blocker = getNoteMedicationSubmitBlocker(entry.note);
        if (blocker?.messageKey) {
          setSaveError(t(blocker.messageKey));
          return;
        }
      }
    }

    let saved = 0;

    sections.forEach((item, sectionIndex) => {
      const patientMeta = patients[sectionIndex] || {
        name: `Patient ${sectionIndex + 1}`,
        room: null,
      };
      (item.generatedNotes || []).forEach((entry, entryIndex) => {
        if (!entry?.note) return;
        onSaveNote(
          buildSavePayload({
            item,
            entry,
            entryIndex,
            patientMeta,
            documentationTime,
            language,
            admittedPatients,
          })
        );
        saved += 1;
      });
    });

    if (!saved) {
      setSaveError(t('fillOneField') || 'Nothing to save.');
      return;
    }
    onSaveComplete?.();
  };

  const medicationSubmitBlocker = useMemo(() => {
    for (const item of sections) {
      for (const entry of item.generatedNotes || []) {
        if (entry.type !== 'medication_admin' || !entry.note) continue;
        const blocker = getNoteMedicationSubmitBlocker(entry.note);
        if (blocker) return blocker;
      }
    }
    return null;
  }, [sections]);

  const saveDisabled = Boolean(medicationSubmitBlocker);
  const saveDisabledLabel = medicationSubmitBlocker?.messageKey
    ? t(medicationSubmitBlocker.messageKey)
    : null;

  const scrollToPatient = (index) => {
    const el = document.getElementById(`queue-patient-${index}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleMicTranscript = useCallback(
    async (liveTranscript) => {
      if (!liveTranscript?.trim()) return;
      setMicBusy(true);
      setSaveError('');

      try {
        let cleaned = liveTranscript.trim();
        try {
          cleaned = (await cleanTranscript(cleaned)) || cleaned;
        } catch {
          // keep raw
        }

        const targetIndex = findSectionIndexForTranscript(
          sections,
          patients,
          cleaned,
          admittedPatients
        );
        const target = sections[targetIndex];
        const roster = target?.patientId
          ? getPatientById(target.patientId, admittedPatients)
          : null;

        const existingNotes = target?.generatedNotes || [];
        let nextNotes = existingNotes;
        if (existingNotes.some((entry) => entry?.note)) {
          nextNotes = await updateNurseNotesFromCorrection(existingNotes, liveTranscript.trim());
        } else {
          const types = await detectNoteTypes(cleaned);
          if (!types.length) return;

          const generated = await Promise.all(
            types.map(async (type) => {
              try {
                let note = validateNote(await generateNote(cleaned, type), type);
                if (roster) {
                  const ctx = toNotePatientContext(roster);
                  note =
                    type === 'handover'
                      ? applyKnownPatientToHandoverNote(note, ctx)
                      : applyKnownPatientToNote(note, ctx);
                }
                return { type, note, error: null };
              } catch (err) {
                return { type, note: null, error: err.message };
              }
            })
          );

          nextNotes = [...existingNotes];
          generated.forEach((entry) => {
            if (!entry.note) return;
            const existing = nextNotes.findIndex((e) => e.type === entry.type);
            if (existing >= 0) {
              nextNotes[existing] = entry;
            } else {
              nextNotes.push(entry);
            }
          });
        }

        setSections((prev) =>
          prev.map((item, index) => {
            if (index !== targetIndex) return item;
            return {
              ...item,
              transcriptRaw: `${item.transcriptRaw || ''}\n${liveTranscript}`.trim(),
              transcriptCleaned: `${item.transcriptCleaned || ''}\n${cleaned}`.trim(),
              generatedNotes: nextNotes,
            };
          })
        );

        scrollToPatient(targetIndex);
      } catch (err) {
        console.error('Multi-patient mic update failed:', err);
        setSaveError(err.message || 'Could not apply recording.');
      } finally {
        setMicBusy(false);
      }
    },
    [admittedPatients, language, patients, sections]
  );

  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden ${REVIEW_PAGE_BG}`}>
      <PatientHeaderBar
        patient={{ name: `${patients.length} patients`, room: null }}
        noteTypes={noteTypes}
        allowEdit={false}
        patients={patients}
        onSelectPatient={scrollToPatient}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-2.5">
        {sharedTranscriptCleaned || sharedTranscriptRaw ? (
          <div className="mb-2.5">
            <TranscriptViewer
              transcriptRaw={sharedTranscriptRaw}
              transcriptCleaned={sharedTranscriptCleaned}
            />
          </div>
        ) : null}

        {micBusy ? <NoteApplyingSkeleton /> : null}

        {sections.map((item, sectionIndex) => {
          const meta = patients[sectionIndex] || {
            name: `Patient ${sectionIndex + 1}`,
            room: null,
          };
          const notes = (item.generatedNotes || []).filter((entry) => entry?.note);
          const hasDedicatedVitals = notes.some((entry) => entry.type === 'vitals');

          return (
            <section
              key={item.noteId || item.encounterGroupId || `section-${sectionIndex}`}
              id={`queue-patient-${sectionIndex}`}
              className={`scroll-mt-3 ${sectionIndex > 0 ? 'mt-6 border-t border-gray-100 pt-5' : ''}`}
            >
              <div className="mb-2.5">
                <p className="font-bold text-gray-900" dir="auto">
                  {meta.name}
                </p>
                {meta.room ? (
                  <p className="text-sm text-gray-500">
                    {t('room')} {meta.room}
                  </p>
                ) : null}
              </div>

              {notes.map((entry) => {
                const noteForDisplay = applyLateEntryMetadata(
                  {
                    ...entry.note,
                    care_time:
                      entry.note.care_time
                      || deriveCareTime(entry.note)
                      || getCurrentCareTime(),
                  },
                  documentationTime
                );

                return (
                  <div key={`${entry.type}-${sectionIndex}`} className="mb-4 space-y-2.5">
                    <NoteTypeSectionHeader noteType={entry.type} />
                    <CareTimeEditor
                      careTime={noteForDisplay.care_time}
                      careDayOffset={noteForDisplay.care_day_offset}
                      documentationTime={documentationTime}
                      onChange={(timingUpdate) =>
                        updateCareTime(sectionIndex, entry.type, timingUpdate)
                      }
                    />
                    {renderNoteEditor(
                      entry.type,
                      entry.note,
                      (updated) => updateNote(sectionIndex, entry.type, updated),
                      {
                        hideExtractedVitals:
                          hasDedicatedVitals && entry.type === 'progress_note',
                      }
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}

        {saveError ? (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {saveError}
          </div>
        ) : null}
      </div>

      <ActionBar
        fixed={false}
        onSave={handleSave}
        onDiscard={onDiscard}
        saveDisabled={saveDisabled}
        saveDisabledLabel={saveDisabledLabel}
      />
      {floatingSlot
        && createPortal(
          <HoldToRecordButton
            patientId={sections[0]?.patientId || null}
            size="fab"
            anchor="inline"
            holdOptions={{ onTranscript: handleMicTranscript }}
          />,
          floatingSlot
        )}
    </div>
  );
}
